import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/auth'
import { useAppMode } from '@/contexts/appMode'
import { Skeleton } from '@/components/ui/skeleton'

function Spinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  )
}

export function RequireAuth() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <Spinner />
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />
  return <Outlet />
}

/** Where a signed-in partner belongs, given their tier. */
function partnerHome(isMaster: boolean): string {
  return isMaster ? '/master' : '/agent'
}

export function RequireAdmin() {
  const { isAdmin, isAgent, isMaster, loading } = useAuth()
  const mode = useAppMode()
  if (loading) return <Spinner />
  // On a partner subdomain, admins hitting /admin are sent to the partner area.
  if (mode === 'agent') return <Navigate to="/agent" replace />
  if (mode === 'master') return <Navigate to="/master" replace />
  if (!isAdmin) {
    // partner role — send to their own tier's area
    return isAgent ? <Navigate to={partnerHome(isMaster)} replace /> : <Navigate to="/login" replace />
  }
  return <Outlet />
}

export function RequireAgent() {
  const { isAgent, isAdmin, loading } = useAuth()
  const mode = useAppMode()
  if (loading) return <Spinner />
  // In admin mode, agents trying to access /agent are redirected to login (wrong subdomain)
  if (mode === 'admin') {
    if (!isAgent) return <Navigate to="/admin" replace />
  }
  if (!isAgent) {
    return isAdmin ? <Navigate to="/admin" replace /> : <Navigate to="/login" replace />
  }
  return <Outlet />
}

export function RequireMaster() {
  const { isMaster, isAgent, isAdmin, loading } = useAuth()
  if (loading) return <Spinner />
  if (!isMaster) {
    // A plain agent has no master surface; bounce to the area they belong to.
    if (isAgent) return <Navigate to="/agent" replace />
    if (isAdmin) return <Navigate to="/admin" replace />
    return <Navigate to="/login" replace />
  }
  return <Outlet />
}
